import type { ReactNode } from 'react'
import { createBrowserRouter, Navigate, useLocation, type RouteObject } from 'react-router'
import { PermissionGuard, RedirectIfAuthenticated, RequireAuth } from '@concours/ui'
import type { PermissionCode } from '@concours/shared'
import { AdminLayout, AuthLayout } from './layouts/AdminLayout'
import { LivePermissionProvider } from './lib/permissions'
import { DashboardPage } from './pages/DashboardPage'
import { DesignPage } from './pages/DesignPage'
import { AccountPage } from './pages/AccountPage'
import { ComingSoonPage } from './pages/ComingSoonPage'
import { PaymentsPage } from './pages/payments/PaymentsPage'
import { PaymentPage } from './pages/payments/PaymentPage'
import { PaymentMethodsPage } from './pages/payments/PaymentMethodsPage'
import { ForbiddenPage, NotFoundPage } from './pages/NotFoundPage'
import { RolesPage } from './pages/rbac/RolesPage'
import { RoleEditorPage } from './pages/rbac/RoleEditorPage'
import { UsersPage } from './pages/rbac/UsersPage'
import { UserDetailPage } from './pages/rbac/UserDetailPage'
import { AuditPage } from './pages/audit/AuditPage'
import { CompetitionsPage } from './pages/competitions/CompetitionsPage'
import { CompetitionPage } from './pages/competitions/CompetitionPage'
import { CompetitionWizardPage } from './pages/competitions/CompetitionWizardPage'
import { FormsPage } from './pages/forms/FormsPage'
import { FormPage } from './pages/forms/FormPage'
import { DuplicatesPage } from './pages/candidates/DuplicatesPage'
import { ApplicationsPage } from './pages/applications/ApplicationsPage'
import { ApplicationPage } from './pages/applications/ApplicationPage'
import { CorrectionsPage } from './pages/applications/CorrectionsPage'
import { SelectionPage } from './pages/selection/SelectionPage'
import { StagesPage } from './pages/selection/StagesPage'
import { MySheetsPage } from './pages/grading/MySheetsPage'
import { SheetPage } from './pages/grading/SheetPage'
import { CommissionsPage } from './pages/grading/CommissionsPage'
import { CommissionStagePage } from './pages/grading/CommissionStagePage'
import { ResultsPage } from './pages/results/ResultsPage'
import { StageResultsPage } from './pages/results/StageResultsPage'
import { ApprovalsPage } from './pages/results/ApprovalsPage'
import { DocumentsHubPage } from './pages/documents/DocumentsHubPage'
import { CentersPage } from './pages/documents/CentersPage'
import { StageDocumentsPage } from './pages/documents/StageDocumentsPage'
import { TemplatesPage } from './pages/notifications/TemplatesPage'
import { TemplateEditorPage } from './pages/notifications/TemplateEditorPage'
import { ProvidersPage } from './pages/notifications/ProvidersPage'
import { HistoryPage } from './pages/notifications/HistoryPage'
import { CampaignsPage } from './pages/notifications/CampaignsPage'
import { ExportsPage } from './pages/reports/ExportsPage'
import { LoginPage } from './pages/auth/LoginPage'
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage'
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage'
import { InvitationPage } from './pages/auth/InvitationPage'
import { DOCUMENT_PERMISSIONS, PLANNED_MODULES } from './navigation'

/** Tout le portail admin exige une session (comptes internes uniquement). */
function Protected({ children }: { children: ReactNode }) {
  const location = useLocation()
  return (
    <RequireAuth
      fallback={<Navigate to="/connexion" replace state={{ from: location.pathname }} />}
    >
      {children}
    </RequireAuth>
  )
}

function GuestOnly({ children }: { children: ReactNode }) {
  // Une fois connecté, on rejoint la page initialement demandée (et non toujours l'accueil).
  const location = useLocation()
  const from = (location.state as { from?: string } | null)?.from ?? '/'
  return (
    <RedirectIfAuthenticated to={<Navigate to={from} replace />}>
      {children}
    </RedirectIfAuthenticated>
  )
}

/** Garde de page : 403 si la permission manque (l'API refuse de toute façon). */
function Guard({
  perm,
  mode = 'all',
  children,
}: {
  perm: PermissionCode | PermissionCode[]
  mode?: 'all' | 'any'
  children: ReactNode
}) {
  return (
    <PermissionGuard perm={perm} mode={mode} forbidden={<ForbiddenPage />}>
      {children}
    </PermissionGuard>
  )
}

export const routes: RouteObject[] = [
  {
    element: <AuthLayout />,
    children: [
      {
        path: 'connexion',
        element: (
          <GuestOnly>
            <LoginPage />
          </GuestOnly>
        ),
      },
      { path: 'mot-de-passe-oublie', element: <ForgotPasswordPage /> },
      { path: 'reinitialisation', element: <ResetPasswordPage /> },
      {
        path: 'invitation',
        element: <InvitationPage />,
      },
    ],
  },
  {
    element: (
      <Protected>
        <LivePermissionProvider>
          <AdminLayout />
        </LivePermissionProvider>
      </Protected>
    ),
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'compte', element: <AccountPage /> },
      { path: 'design', element: <DesignPage /> },
      {
        path: 'profils',
        element: (
          <Guard perm="roles.view">
            <RolesPage />
          </Guard>
        ),
      },
      {
        path: 'profils/:id',
        element: (
          <Guard perm="roles.view">
            <RoleEditorPage />
          </Guard>
        ),
      },
      {
        path: 'utilisateurs',
        element: (
          <Guard perm="users.view">
            <UsersPage />
          </Guard>
        ),
      },
      {
        path: 'concours',
        element: (
          <Guard perm="concours.view">
            <CompetitionsPage />
          </Guard>
        ),
      },
      {
        path: 'concours/nouveau',
        element: (
          <Guard perm="concours.create">
            <CompetitionWizardPage />
          </Guard>
        ),
      },
      {
        path: 'concours/:id',
        element: (
          <Guard perm="concours.view">
            <CompetitionPage />
          </Guard>
        ),
      },
      {
        path: 'formulaires',
        element: (
          <Guard perm="forms.view">
            <FormsPage />
          </Guard>
        ),
      },
      {
        path: 'formulaires/:id',
        element: (
          <Guard perm="forms.view">
            <FormPage />
          </Guard>
        ),
      },
      {
        path: 'candidatures',
        element: (
          <Guard perm="candidature.view">
            <ApplicationsPage />
          </Guard>
        ),
      },
      {
        path: 'candidatures/:id',
        element: (
          <Guard perm="candidature.view">
            <ApplicationPage />
          </Guard>
        ),
      },
      {
        path: 'corrections',
        element: (
          <Guard perm="corrections.view">
            <CorrectionsPage />
          </Guard>
        ),
      },
      {
        path: 'selection',
        element: (
          <Guard perm="selection.view">
            <SelectionPage />
          </Guard>
        ),
      },
      {
        path: 'selection/:competitionId',
        element: (
          <Guard perm="selection.view">
            <StagesPage />
          </Guard>
        ),
      },
      {
        path: 'notes',
        element: (
          <Guard perm="notes.enter">
            <MySheetsPage />
          </Guard>
        ),
      },
      {
        path: 'notes/releves/:sheetId',
        element: (
          <Guard perm={['notes.enter', 'notes.validate', 'evaluators.assign']} mode="any">
            <SheetPage />
          </Guard>
        ),
      },
      {
        path: 'commissions',
        element: (
          <Guard perm={['notes.validate', 'evaluators.assign']} mode="any">
            <CommissionsPage />
          </Guard>
        ),
      },
      {
        path: 'commissions/:stageId',
        element: (
          <Guard perm={['notes.validate', 'evaluators.assign']} mode="any">
            <CommissionStagePage />
          </Guard>
        ),
      },
      {
        path: 'resultats',
        element: (
          <Guard perm="results.view">
            <ResultsPage />
          </Guard>
        ),
      },
      {
        path: 'resultats/approbations',
        element: (
          <Guard perm={['results.approve', 'results.publish']} mode="any">
            <ApprovalsPage />
          </Guard>
        ),
      },
      {
        path: 'resultats/:stageId',
        element: (
          <Guard perm="results.view">
            <StageResultsPage />
          </Guard>
        ),
      },
      {
        path: 'documents',
        element: (
          <Guard perm={DOCUMENT_PERMISSIONS} mode="any">
            <DocumentsHubPage />
          </Guard>
        ),
      },
      {
        path: 'documents/concours/:competitionId',
        element: (
          <Guard perm={['centers.manage', 'convocations.generate']} mode="any">
            <CentersPage />
          </Guard>
        ),
      },
      {
        path: 'documents/etapes/:stageId',
        element: (
          <Guard perm={DOCUMENT_PERMISSIONS} mode="any">
            <StageDocumentsPage />
          </Guard>
        ),
      },
      {
        path: 'exports',
        element: (
          <Guard perm="reports.export">
            <ExportsPage />
          </Guard>
        ),
      },
      {
        path: 'notifications/historique',
        element: (
          <Guard perm={['notifications.send', 'notifications.templates.manage']} mode="any">
            <HistoryPage />
          </Guard>
        ),
      },
      {
        path: 'notifications/envois',
        element: (
          <Guard perm="notifications.send">
            <CampaignsPage />
          </Guard>
        ),
      },
      {
        path: 'notifications/modeles',
        element: (
          <Guard perm="notifications.templates.manage">
            <TemplatesPage />
          </Guard>
        ),
      },
      {
        path: 'notifications/modeles/:event',
        element: (
          <Guard perm="notifications.templates.manage">
            <TemplateEditorPage />
          </Guard>
        ),
      },
      {
        path: 'notifications/fournisseurs',
        element: (
          <Guard perm="notification_providers.manage">
            <ProvidersPage />
          </Guard>
        ),
      },
      {
        path: 'paiements',
        element: (
          <Guard perm="payment.view">
            <PaymentsPage />
          </Guard>
        ),
      },
      {
        path: 'paiements/:id',
        element: (
          <Guard perm="payment.view">
            <PaymentPage />
          </Guard>
        ),
      },
      {
        path: 'moyens-paiement',
        element: (
          <Guard perm="payment_methods.view">
            <PaymentMethodsPage />
          </Guard>
        ),
      },
      {
        path: 'doublons',
        element: (
          <Guard perm="candidates.duplicates.review">
            <DuplicatesPage />
          </Guard>
        ),
      },
      {
        path: 'audit',
        element: (
          <Guard perm="audit.view">
            <AuditPage />
          </Guard>
        ),
      },
      {
        path: 'utilisateurs/:id',
        element: (
          <Guard perm="users.view">
            <UserDetailPage />
          </Guard>
        ),
      },
      ...PLANNED_MODULES.map((module) => ({
        path: `${module.href.slice(1)}/*`,
        element: <ComingSoonPage navKey={module.navKey} phase={module.phase} />,
      })),
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]

export const router = createBrowserRouter(routes)
